import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import { useTelegram } from '../context/TelegramContext';
import { useCurrency } from '../context/CurrencyContext';
import BudgetForm from '../components/budget/BudgetForm';
import BudgetList from '../components/budget/BudgetList';
import BudgetModal from '../components/budget/BudgetModal';

const Container = styled.div`
  padding: 16px;
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #4a6fa1;
  margin: 0;
`;

const BackButton = styled(Link)`
  display: inline-block;
  padding: 8px 16px;
  background: #f0f3f9;
  color: #4a6fa1;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background: #e6ebf5;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1),
                -1px -1px 3px rgba(255, 255, 255, 0.9);
  }
  
  &:active {
    box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.1),
                inset -1px -1px 3px rgba(255, 255, 255, 0.9);
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #4a6fa1;
  margin: 30px 0 15px;
`;

// Исправленные стили для переключателя связанных аккаунтов
const SharedDataToggle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: linear-gradient(145deg, #f8f9fa, #ffffff);
  border-radius: 12px;
  border: 1px solid #e9ecef;
  box-shadow: 
    3px 3px 6px rgba(0, 0, 0, 0.05),
    -3px -3px 6px rgba(255, 255, 255, 0.8);
`;

const SwitchLabel = styled.span`
  font-weight: 500;
  color: #4a6fa1;
  font-size: 14px;
  flex: 1;
`;

const SwitchToggle = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 25px;
  cursor: pointer;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 25px;
    
    &:before {
      position: absolute;
      content: "";
      height: 19px;
      width: 19px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
    
    &:hover {
      background-color: #b3b3b3;
    }
  }
  
  input:checked + .slider {
    background-color: #4a6fa1;
    
    &:hover {
      background-color: #3a5a91;
    }
  }
  
  input:checked + .slider:before {
    transform: translateX(25px);
  }
`;

const Budget = () => {
  const { t } = useTranslation();
  const { telegramUserId } = useTelegram();
  const currencyContext = useCurrency();
  
  // Получаем актуальную валюту из разных источников с приоритетом
  const [userCurrency, setUserCurrency] = useState(() => {
    const storedCurrency = localStorage.getItem('preferred_currency') || 
                           localStorage.getItem('user_currency');
                           
    return storedCurrency || 
           (currencyContext?.userCurrency || currencyContext?.preferredCurrency || 'USD');
  });
  
  // Отслеживаем изменения валюты в localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if ((e.key === 'preferred_currency' || e.key === 'user_currency') && e.newValue) {
        if (e.newValue !== userCurrency) {
          setUserCurrency(e.newValue);
        }
      }
    };
    
    const handleCurrencyChange = () => {
      const storedCurrency = localStorage.getItem('preferred_currency') || 
                            localStorage.getItem('user_currency');
      if (storedCurrency && storedCurrency !== userCurrency) {
        setUserCurrency(storedCurrency);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('currencyChanged', handleCurrencyChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('currencyChanged', handleCurrencyChange);
    };
  }, [userCurrency]);
  
  // Синхронизируем с контекстом при его изменении
  useEffect(() => {
    const contextCurrency = currencyContext?.userCurrency || 
                           currencyContext?.preferredCurrency;
                           
    if (contextCurrency && contextCurrency !== userCurrency) {
      setUserCurrency(contextCurrency);
    }
  }, [currencyContext, userCurrency]);
  
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [exchangeRates, setExchangeRates] = useState({});
  
  // Состояние для управления связанными аккаунтами
  const [includeSharedData, setIncludeSharedData] = useState(() => {
    const stored = localStorage.getItem('includeSharedBudgetData');
    return stored === 'true';
  });
  
  const [hasLinkedAccounts, setHasLinkedAccounts] = useState(false);
  
  // Модальное окно
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentBudget, setCurrentBudget] = useState({
    category: '',
    limitAmount: 0,
    currency: userCurrency || 'UAH'
  });

  // Функция переключения
  const handleToggleSharedData = () => {
    const newValue = !includeSharedData;
    setIncludeSharedData(newValue);
    localStorage.setItem('includeSharedBudgetData', newValue.toString());
  };

  // Проверяем наличие связанных аккаунтов
  const checkLinkedAccounts = async () => {
    try {
      const response = await fetch(`https://fftg.farodinprojects.org:3000/get_linked_accounts?user_id=${telegramUserId}`);
      
      if (response.ok) {
        const data = await response.json();
        const hasLinked = data && Array.isArray(data) && data.length > 0;
        setHasLinkedAccounts(hasLinked);
      } else {
        setHasLinkedAccounts(false);
      }
    } catch (error) {
      console.error('Error checking linked accounts:', error);
      setHasLinkedAccounts(false);
    }
  };

  // Обновление валюты в currentBudget при изменении userCurrency
  useEffect(() => {
    if (userCurrency && !currentBudget.category) {
      setCurrentBudget(prev => ({
        ...prev,
        currency: userCurrency
      }));
    }
  }, [userCurrency, currentBudget.category]);

  // Загрузка категорий
  const loadCategories = async () => {
    try {
      const response = await fetch(`https://fftg.farodinprojects.org:3000/categories?user_id=${telegramUserId}&type=expense`);
      if (!response.ok) throw new Error('Failed to load categories');
      
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError(t('category_load_error'));
    }
  };

  // Загрузка курсов валют
  const loadExchangeRates = async () => {
    try {
      const response = await fetch('https://fftg.farodinprojects.org:3000/exchange_rates');
      if (!response.ok) throw new Error('Failed to load exchange rates');
      
      const data = await response.json();
      setExchangeRates(data.rates || {});
    } catch (error) {
      console.error('Error loading exchange rates:', error);
    }
  };

  // Загрузка бюджетов с учетом связанных аккаунтов
  const loadBudgets = async () => {
    setIsLoading(true);
    
    try {
      const url = `https://fftg.farodinprojects.org:3000/get_budget?user_id=${telegramUserId}&shared=${includeSharedData}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load budgets');
      
      const data = await response.json();
      setBudgets(data.budgets || []);
    } catch (error) {
      console.error('Error loading budgets:', error);
      setError(t('budget_load_error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Следим за изменениями includeSharedData
  useEffect(() => {
    if (telegramUserId) {
      loadBudgets();
    }
  }, [includeSharedData, telegramUserId]);

  // Добавление/обновление бюджета
  const saveBudget = async (category, limitAmount, currency) => {
    try {
      setIsLoading(true);
      
      const response = await fetch('https://fftg.farodinprojects.org:3000/set_budget', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: telegramUserId,
          category,
          limit_amount: limitAmount,
          currency
        })
      });
      
      if (!response.ok) throw new Error('Failed to save budget');
      
      await loadBudgets();
      return true;
    } catch (error) {
      console.error('Error saving budget:', error);
      setError(t('budget_save_error'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Удаление бюджета
  const deleteBudget = async (category) => {
    if (!window.confirm(`${t('confirm_delete')} "${category}"?`)) return;
    
    try {
      setIsLoading(true);
      
      const response = await fetch('https://fftg.farodinprojects.org:3000/delete_budget', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: telegramUserId,
          category
        })
      });
      
      if (!response.ok) throw new Error('Failed to delete budget');
      
      alert(t('budget_deleted'));
      await loadBudgets();
    } catch (error) {
      console.error('Error deleting budget:', error);
      alert(t('delete_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Открытие модального окна для редактирования
  const openEditModal = (category, limitAmount, currency) => {
    setCurrentBudget({
      category,
      limitAmount,
      currency: currency || userCurrency || 'UAH'
    });
    setIsModalOpen(true);
  };

  // Обработка отправки формы в модальном окне
  const handleModalSubmit = async (newLimit, currency) => {
    const success = await saveBudget(currentBudget.category, newLimit, currency);
    if (success) {
      setIsModalOpen(false);
    }
  };

  // Загрузка данных при первом рендере
  useEffect(() => {
    if (telegramUserId) {
      checkLinkedAccounts();
      loadCategories();
      loadExchangeRates();
      loadBudgets();
    }
  }, [telegramUserId]);

  return (
    <Layout>
      <Container>
        <PageHeader>
          <PageTitle>{t('budget_settings')}</PageTitle>
          <BackButton to="/settings">{t('back')}</BackButton>
        </PageHeader>

        {/* Переключатель для связанных аккаунтов */}
        {hasLinkedAccounts && (
          <SharedDataToggle>
            <SwitchLabel>
              {t('include_shared_budgets')}
            </SwitchLabel>
            <SwitchToggle>
              <input 
                type="checkbox" 
                checked={includeSharedData}
                onChange={handleToggleSharedData}
              />
              <span className="slider"></span>
            </SwitchToggle>
          </SharedDataToggle>
        )}

        <BudgetForm
          categories={categories}
          onSave={saveBudget}
          defaultCurrency={userCurrency || 'UAH'}
        />

        <SectionTitle>
          {includeSharedData && hasLinkedAccounts ? 
            t('shared_budgets') : 
            t('your_budget')
          }
        </SectionTitle>
        
        <BudgetList
          budgets={budgets}
          isLoading={isLoading}
          exchangeRates={exchangeRates}
          defaultCurrency={userCurrency || 'UAH'}
          onEdit={openEditModal}
          onDelete={deleteBudget}
          showSharedInfo={includeSharedData && hasLinkedAccounts}
        />

        <BudgetModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleModalSubmit}
          initialLimit={currentBudget.limitAmount}
          initialCurrency={currentBudget.currency}
          category={currentBudget.category}
        />

        {error && (
          <div style={{ 
            color: '#dc3545', 
            background: '#f8d7da', 
            padding: '10px', 
            borderRadius: '5px', 
            marginTop: '20px' 
          }}>
            {error}
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default Budget;